import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createTemplateRequest } from '../../../store/whatsappSlice';
import { useRegularTemplate } from './useRegularTemplate';
import { useAuthTemplate } from './useAuthTemplate';
import { buildAuthTemplatePayload, buildRegularTemplatePayload } from '../utils/payloadBuilder';
import { AUTH_TEMPLATE_DEFAULTS, REGULAR_TEMPLATE_DEFAULTS, getAuthOtpButtonText } from '../constants/templateConstants';

const initialFormData = {
  name: '',
  category: 'utility',
  language: 'en',
  bodyText: '',
  ...REGULAR_TEMPLATE_DEFAULTS,
  ...AUTH_TEMPLATE_DEFAULTS,
};

export const useCreateTemplate = () => {
  const dispatch = useDispatch();
  const mediaState = useSelector((state) => state.media);
  const loading = useSelector((state) => state.whatsapp.loading);
  const success = useSelector((state) => state.whatsapp.success);

  const [formData, setFormDataState] = useState(initialFormData);
  const [validationErrors, setValidationErrors] = useState({});

  const reset = () => {
    setFormDataState(initialFormData);
    setValidationErrors({});
  };

  const setFormData = (field, value) => {
    setFormDataState((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'category' && value === 'authentication') {
        next.bodyText = '';
        next.otpButtonText = getAuthOtpButtonText(next.language);
      }
      if (field === 'language' && next.category === 'authentication') {
        next.otpButtonText = getAuthOtpButtonText(value);
      }
      return next;
    });
    if (validationErrors[field]) setValidationErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const setMultipleFormData = (updates) => {
    setFormDataState((prev) => {
      let next = { ...prev, ...updates };
      if (updates.category === 'authentication') next.bodyText = '';
      return next;
    });
    
    // Clear validation errors for updated fields
    setValidationErrors((prev) => {
      const next = { ...prev };
      Object.keys(updates).forEach(key => {
        delete next[key];
      });
      return next;
    });
  };

  const isAuthTemplate = formData.category === 'authentication';

  const regularTemplate = useRegularTemplate(formData, setFormData, setValidationErrors);
  const authTemplate = useAuthTemplate(formData, setValidationErrors);

  const submit = () => {
    if (loading) return;

    if (isAuthTemplate) {
      if (!authTemplate.validateForm()) return;
      const payload = buildAuthTemplatePayload(formData);
      dispatch(createTemplateRequest(payload));
    } else {
      if (!regularTemplate.validateForm()) return;
      if (!regularTemplate.validateSamples()) return;
      const payload = buildRegularTemplatePayload(formData, mediaState);
      dispatch(createTemplateRequest(payload));
    }
  };

  return {
    formData,
    setFormData,
    setMultipleFormData,
    validationErrors,
    reset,
    isAuthTemplate,
    headerVars: regularTemplate.headerVars,
    bodyVars: regularTemplate.bodyVars,
    sampleValues: regularTemplate.sampleValues,
    sampleErrors: regularTemplate.sampleErrors,
    handleSampleChange: regularTemplate.handleSampleChange,
    sampleValidationMessage: regularTemplate.sampleValidationMessage,
    setSampleValidationMessage: regularTemplate.setSampleValidationMessage,
    localHeaderError: regularTemplate.localHeaderError,
    localBodyError: regularTemplate.localBodyError,
    handleHeaderTextChange: regularTemplate.handleHeaderTextChange,
    loading,
    submit,
    success,
  };
};
